import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role hierarchy: propietario > master > admin > consulta
const ROLE_HIERARCHY = {
  propietario: 0,
  master: 1,
  admin: 2,
  consulta: 3
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, nombre, rol = 'consulta') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, nombre, rol });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Role check functions
  const isPropietario = () => user?.rol === 'propietario';
  const isMaster = () => user?.rol === 'master';
  const isAdmin = () => user?.rol === 'admin';
  const isConsulta = () => user?.rol === 'consulta';
  
  // Check if user has at least this role level (for permission checks)
  const hasRole = (minRole) => {
    if (!user) return false;
    const userLevel = ROLE_HIERARCHY[user.rol];
    const minLevel = ROLE_HIERARCHY[minRole];
    return userLevel <= minLevel;
  };
  
  // Can create parks (propietario and master)
  const canCreateParks = () => hasRole('master');
  
  // Can manage users (propietario and master)
  const canManageUsers = () => hasRole('master');
  
  // Can edit operational data (propietario, master, and admin)
  const canEditData = () => hasRole('admin');

  const contextValue = useMemo(() => ({
    user, 
    token, 
    loading, 
    login, 
    register, 
    logout, 
    isPropietario,
    isMaster,
    isAdmin,
    isConsulta,
    hasRole,
    canCreateParks,
    canManageUsers,
    canEditData
  }), [user, token, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
